import { SearchProvider } from "@elastic/react-search-ui";
import CodesystemTabNav from "components/CodeSystemTabNav";
import CodeSystemTabView from "components/CodeSystemTabView";
import { ElasticWrapper } from "components/common";
import { useEsConfig } from "context/elasticsearch/esConfigContext";
import getSearchDriverOptions from "context/elasticsearch/searchProviderConfig/searchOptionsFactory";
import { CodeSystem } from "context/elasticsearch/types";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCodeSystemName } from "utils";

import CodeSystemSearchHeader from "./searchHeader";
import StateSync from "./stateSync";

type CodeSystemPageProps = {
  codeSystem: CodeSystem;
};

const CodeSystemPage = ({ codeSystem }: CodeSystemPageProps) => {
  const { t } = useTranslation();
  const { config } = useEsConfig(codeSystem);

  useEffect(() => {
    const kodeverk = getCodeSystemName(codeSystem as string);
    document.title = `${t("general.finnkode")} - ${kodeverk}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeSystem]);

  // whenever `config` changes, we bump reloadKey and recompute options
  // this is primarily used for development and hot-reloading of config changes
  const [reloadKey, setReloadKey] = useState(0);
  const [options, setOptions] = useState(() => getSearchDriverOptions(config));
  const prevConfigRef = useRef(config);

  useEffect(() => {
    // Only update if config reference actually changed
    if (prevConfigRef.current === config) {
      return;
    }
    prevConfigRef.current = config;
    setOptions(getSearchDriverOptions(config));
    setReloadKey((k) => k + 1);
  }, [config]);

  const isSmallLabCodeSystem =
    codeSystem === CodeSystem.Provemateriale ||
    codeSystem === CodeSystem.Anatomisk ||
    codeSystem === CodeSystem.Tekstligeresultatverdier ||
    codeSystem === CodeSystem.Undersokelsesmetode;

  const isClinicalCodeSystem =
    codeSystem === CodeSystem.Icd10 ||
    codeSystem === CodeSystem.Icpc2 ||
    codeSystem === CodeSystem.Nkpk ||
    codeSystem === CodeSystem.Phbu;

  // Clinical code systems and small lab code systems show tab navigation
  const shouldShowTabNav = isClinicalCodeSystem || isSmallLabCodeSystem;

  return (
    <>
      {/* Keeps codeValue in sync with URL param without triggering unnecessary renders */}
      <StateSync codeSystem={codeSystem} />

      {/* max-height:90vh */}
      <div className="p-2">
        {shouldShowTabNav ? (
          // Tab navigation between code systems at top of page
          <CodesystemTabNav />
        ) : null}

        {/* SearchProvider is a wrapper for the search functionality.
				It encapsulates searchTerm, results, and other elasticsearch data
				in a context that can be access by child components using the withSearch HOC.
				For an example see export statement of TabView component.
			*/}
        <SearchProvider
          key={reloadKey}
          config={{
            initialState: {
              searchTerm: "",
              resultsPerPage: isSmallLabCodeSystem ? 350 : 20
            },
            ...options
          }}
        >
          <ElasticWrapper>
            {/* Heading and search bar at top of page */}
            <CodeSystemSearchHeader codeSystem={codeSystem} />
            {/* Tabs with tree view, search results, and history tabs - responsible for tab switching, including on search  */}
            {<CodeSystemTabView codeSystem={codeSystem} displayScore={false} />}
          </ElasticWrapper>
        </SearchProvider>
      </div>
    </>
  );
};

export default CodeSystemPage;
